import {
  AudioLinesIcon,
  BrainIcon,
  BriefcaseIcon,
  CpuIcon,
  FileTextIcon,
  ListOrderedIcon,
  MegaphoneIcon,
  PhoneIncomingIcon,
  PlusIcon,
  RadioIcon,
  TimerIcon,
  Trash2Icon,
  UserRoundIcon,
} from "lucide-react";

import { Column } from "@/components/Column";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { SadieSimulatorEditedChip } from "@/components/tools/sadie-simulator/SadieSimulatorEditedChip";
import {
  FIELD_CONTENT_CLASS,
  FIELD_ICON_CLASS,
  FIELD_TRIGGER_CLASS,
  SadieSimulatorField as SimulatorField,
  SadieSimulatorSwitchRow,
} from "@/components/tools/sadie-simulator/SadieSimulatorField";
import { SadieSimulatorResetButton } from "@/components/tools/sadie-simulator/SadieSimulatorResetButton";
import { Button } from "@ui/button";
import { Input } from "@ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";
import { Textarea } from "@ui/textarea";

import {
  SadieVoiceCustomNote,
  useSadieVoiceSimulator,
} from "@/hooks/useSadieVoiceSimulator";
import { cn } from "@/lib/utils";
import { SadieVoiceEngine } from "@/types";

const ENGINES: { value: SadieVoiceEngine; label: string }[] = [
  { value: "live", label: "GPT-Live: Sadie voice v1" },
  { value: "realtime", label: "Realtime: playground" },
];

const NOTES_AT = [
  { value: "startup", label: "In the startup input (before she speaks)" },
  { value: "pushed", label: "Pushed after start (thinking.append)" },
];

// A labelled select of plain options.
const OptionSelect = ({
  disabled,
  icon,
  id,
  label,
  onChange,
  options,
  value,
}: {
  disabled: boolean;
  icon: React.ReactElement<{ className?: string }>;
  id: string;
  label: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  value: string | undefined;
}) => (
  <SimulatorField icon={icon} id={id} label={label}>
    <Select disabled={disabled} onValueChange={onChange} value={value}>
      <SelectTrigger className={FIELD_TRIGGER_CLASS} id={id}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className={FIELD_CONTENT_CLASS}>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </SimulatorField>
);

// An editable prompt or note, with its edited chip and reset.
const PromptField = ({
  description,
  disabled,
  edited,
  label,
  minHeightClass = "min-h-[220px]",
  onChange,
  onReset,
  tokenLimit,
  tokens,
  value,
}: {
  description?: string;
  disabled: boolean;
  edited: boolean;
  label: string;
  minHeightClass?: string;
  onChange: (value: string) => void;
  onReset: () => void;
  tokenLimit?: number;
  tokens?: number;
  value: string;
}) => (
  <Column className="gap-1.5">
    <Row className="h-7 items-center justify-between">
      <Row className="items-center gap-2">
        <FileTextIcon className={FIELD_ICON_CLASS} />
        <Text className="text-sm font-medium text-slate-700">{label}</Text>
        {edited ? <SadieSimulatorEditedChip /> : null}
      </Row>
      <Row className="items-center gap-2">
        {tokens !== undefined && tokenLimit !== undefined ? (
          <Text
            className={cn(
              "font-mono text-[11px]",
              tokens > tokenLimit ? "text-red-600" : "text-slate-400",
            )}
          >
            ~{tokens}/{tokenLimit} tokens
          </Text>
        ) : null}
        <SadieSimulatorResetButton
          disabled={disabled || !edited}
          onClick={onReset}
        />
      </Row>
    </Row>
    {description ? (
      <Text className="text-xs text-slate-500">{description}</Text>
    ) : null}
    <Textarea
      className={cn("bg-white font-mono text-xs", minHeightClass)}
      disabled={disabled}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      value={value}
    />
  </Column>
);

// A note the user wrote. It goes in after the filled ones and is kept in
// this browser, so it outlives caller changes and reloads.
const CustomNoteField = ({
  disabled,
  note,
  onChange,
  onRemove,
  tokenLimit,
}: {
  disabled: boolean;
  note: SadieVoiceCustomNote & { tokens: number };
  onChange: (patch: { label?: string; content?: string }) => void;
  onRemove: () => void;
  tokenLimit: number | undefined;
}) => (
  <Column className="gap-1.5">
    <Row className="items-center gap-2">
      <FileTextIcon className={FIELD_ICON_CLASS} />
      <Input
        aria-label="Note label"
        className="h-7 flex-1 bg-white text-sm"
        disabled={disabled}
        onChange={(e) => {
          onChange({ label: e.target.value });
        }}
        placeholder="Your note's label"
        value={note.label}
      />
      {tokenLimit !== undefined ? (
        <Text
          className={cn(
            "shrink-0 font-mono text-[11px]",
            note.tokens > tokenLimit ? "text-red-600" : "text-slate-400",
          )}
        >
          ~{note.tokens}/{tokenLimit} tokens
        </Text>
      ) : null}
      <Button
        aria-label="Remove note"
        className="h-7 w-7 shrink-0 text-slate-500"
        disabled={disabled}
        onClick={onRemove}
        size="icon"
        variant="ghost"
      >
        <Trash2Icon className="h-3.5 w-3.5" />
      </Button>
    </Row>
    <Text className="text-xs text-slate-500">
      Your own note. Goes in after the filled notes; kept in this browser.
    </Text>
    <Textarea
      className="min-h-[120px] bg-white font-mono text-xs"
      disabled={disabled}
      onChange={(e) => {
        onChange({ content: e.target.value });
      }}
      placeholder="What Sadie should know before the call."
      value={note.content}
    />
  </Column>
);

export const SadieVoiceSimulatorControls = ({
  disabled,
  simulator: sim,
}: {
  // A call is under way: settings apply to the next one.
  disabled: boolean;
  simulator: ReturnType<typeof useSadieVoiceSimulator>;
}) => (
  <Column className="gap-5">
    <Column className="gap-3">
      <OptionSelect
        disabled={disabled}
        icon={<RadioIcon />}
        id="sadie-voice-engine"
        label="Engine"
        onChange={(value) => {
          sim.setEngine(value as SadieVoiceEngine);
        }}
        options={ENGINES}
        value={sim.engine}
      />

      {sim.engine === "live" ? (
        <>
          <OptionSelect
            disabled={disabled}
            icon={<UserRoundIcon />}
            id="sadie-voice-caller"
            label="Caller"
            onChange={sim.setCaller}
            options={sim.callers.map((c) => ({ value: c.key, label: c.label }))}
            value={sim.caller}
          />
          <SadieSimulatorSwitchRow
            checked={sim.knownCaller}
            description="Off: an unknown number (N02, nothing on file)"
            disabled={disabled}
            icon={<PhoneIncomingIcon className={FIELD_ICON_CLASS} />}
            id="sadie-voice-known"
            label="Known caller"
            onCheckedChange={sim.setKnownCaller}
          />
          <SadieSimulatorSwitchRow
            checked={sim.pitch}
            description={
              sim.canPitch
                ? "Matching has a job for them (N04). Off: N05"
                : "No job clears the bar for this caller (N05)"
            }
            disabled={disabled || !sim.canPitch}
            icon={<BriefcaseIcon className={FIELD_ICON_CLASS} />}
            id="sadie-voice-pitch"
            label="Job to pitch"
            onCheckedChange={sim.setPitch}
          />
          <OptionSelect
            disabled={disabled}
            icon={<ListOrderedIcon />}
            id="sadie-voice-notes-at"
            label="Opening notes"
            onChange={(value) => {
              sim.setNotesAt(value as "startup" | "pushed");
            }}
            options={NOTES_AT}
            value={sim.notesAt}
          />
        </>
      ) : null}

      <OptionSelect
        disabled={disabled}
        icon={<CpuIcon />}
        id="sadie-voice-model"
        label={sim.engine === "live" ? "Backend model" : "Model"}
        onChange={sim.setModel}
        options={sim.models.map((m) => ({ value: m, label: m }))}
        value={sim.model}
      />
      <OptionSelect
        disabled={disabled}
        icon={<AudioLinesIcon />}
        id="sadie-voice-voice"
        label="Voice"
        onChange={sim.setVoice}
        options={sim.voices.map((v) => ({ value: v, label: v }))}
        value={sim.voice}
      />

      {sim.engine === "realtime" ? (
        <>
          <OptionSelect
            disabled={disabled}
            icon={<TimerIcon />}
            id="sadie-voice-turn-detection"
            label="Turn detection"
            onChange={sim.setTurnDetection}
            options={sim.turnDetections.map((t) => ({
              value: t.key,
              label: t.label,
            }))}
            value={sim.turnDetection}
          />
          <OptionSelect
            disabled={disabled}
            icon={<BrainIcon />}
            id="sadie-voice-reasoning"
            label="Reasoning effort"
            onChange={sim.setReasoningEffort}
            options={sim.reasoningEfforts.map((r) => ({ value: r, label: r }))}
            value={sim.reasoningEffort}
          />
        </>
      ) : null}

      <SadieSimulatorSwitchRow
        checked={sim.sadieSpeaksFirst}
        description="She answers the call with her opener"
        disabled={disabled}
        icon={<MegaphoneIcon className={FIELD_ICON_CLASS} />}
        id="sadie-voice-speaks-first"
        label="Sadie speaks first"
        onCheckedChange={sim.setSadieSpeaksFirst}
      />
    </Column>

    <div className="border-t border-slate-200" />

    {sim.engine === "realtime" ? (
      <PromptField
        description="One model listens, thinks and speaks."
        disabled={disabled}
        edited={sim.isRealtimePromptEdited}
        label="Prompt"
        onChange={sim.setRealtimePrompt}
        onReset={sim.resetRealtimePrompt}
        value={sim.realtimePrompt}
      />
    ) : sim.bundleStatus === "pending" ? (
      <LoadingIndicator variant="small" />
    ) : sim.bundleStatus === "error" || !sim.bundle ? (
      <Text className="text-sm text-red-600">
        Could not fill the v1 prompts for this caller.
      </Text>
    ) : (
      <Column className="gap-5">
        <Text className="text-xs text-slate-500">
          Filled for this caller. Edits apply to the next call and are replaced
          when the caller setup changes.
        </Text>
        {sim.notes.map((note, i) => (
          <PromptField
            disabled={disabled}
            edited={note.edited}
            key={`${note.key}-${String(i)}`}
            label={`${note.key} · ${note.label}`}
            minHeightClass="min-h-[120px]"
            onChange={(value) => {
              sim.setNote(i, value);
            }}
            onReset={() => {
              sim.resetNote(i);
            }}
            tokenLimit={sim.bundle?.noteTokenLimit}
            tokens={note.tokens}
            value={note.content}
          />
        ))}
        {sim.customNotes.map((note) => (
          <CustomNoteField
            disabled={disabled}
            key={note.id}
            note={note}
            onChange={(patch) => {
              sim.updateCustomNote(note.id, patch);
            }}
            onRemove={() => {
              sim.removeCustomNote(note.id);
            }}
            tokenLimit={sim.bundle?.noteTokenLimit}
          />
        ))}
        <Button
          className="self-start"
          disabled={disabled}
          onClick={sim.addCustomNote}
          size="sm"
          variant="outline"
        >
          <PlusIcon className="mr-1.5 h-4 w-4" />
          Add note
        </Button>
        <PromptField
          description="01 · gpt-live-1's instructions."
          disabled={disabled}
          edited={sim.isLiveFieldEdited("instructions")}
          label="Voice instructions"
          onChange={(value) => {
            sim.setLiveField("instructions", value);
          }}
          onReset={() => {
            sim.resetLiveField("instructions");
          }}
          value={sim.liveField("instructions")}
        />
        <PromptField
          description="03 · send_text and end_call, plus the worker and job records."
          disabled={disabled}
          edited={sim.isLiveFieldEdited("backendInstructions")}
          label="Backend"
          onChange={(value) => {
            sim.setLiveField("backendInstructions", value);
          }}
          onReset={() => {
            sim.resetLiveField("backendInstructions");
          }}
          value={sim.liveField("backendInstructions")}
        />
        <PromptField
          description="04 · runs on the transcript when you click Run post-call."
          disabled={false}
          edited={sim.isLiveFieldEdited("postCallPrompt")}
          label="Post-call"
          onChange={(value) => {
            sim.setLiveField("postCallPrompt", value);
          }}
          onReset={() => {
            sim.resetLiveField("postCallPrompt");
          }}
          value={sim.liveField("postCallPrompt")}
        />
      </Column>
    )}
  </Column>
);
